import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { sellerService } from '../../services/apiService';

const SellerContainer = styled.div`
  padding: 2rem;
  max-width: 900px;
  margin: 0 auto;
`;

const Form = styled.form`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
    color: #333;
  }

  input, textarea, select {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: #667eea;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }
  }

  textarea {
    min-height: 100px;
    resize: vertical;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 0.7rem;
  background: #1e293b;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.35px;

  &:hover {
    background: #0f172a;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const WarningBox = styled.div`
  padding: 1rem;
  background: #fffbea;
  border: 1px solid #ffd666;
  border-radius: 4px;
  color: #ad6800;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
`;

const SellerListingForm = ({ onListingCreated }) => {
  const [farms, setFarms] = useState([]);
  const [selectedFarmId, setSelectedFarmId] = useState('');
  const [availableCrops, setAvailableCrops] = useState([]);
  const [loadingFarms, setLoadingFarms] = useState(true);
  const [farmsError, setFarmsError] = useState('');

  const [formData, setFormData] = useState({
    cropType: '',
    farmId: '',
    breedVariety: '',
    farmingDetails: '',
    cultivationArea: '',
    harvestQuantity: '',
    moq: '',
    basePrice: '',
    certificationUrl: '',
    state: '',
    region: '',
  });
  const [priceTiers, setPriceTiers] = useState([{ minQuantity: '', price: '' }]);
  const [submitStatus, setSubmitStatus] = useState('');

  // Fetch seller's registered farms on component mount
  useEffect(() => {
    fetchSellerFarms();
  }, []);

  const fetchSellerFarms = async () => {
    try {
      setLoadingFarms(true);
      const farmsData = await sellerService.getFarms();
      setFarms(farmsData || []);
      setFarmsError('');
    } catch (error) {
      console.error('Error fetching farms:', error);
      setFarmsError('Unable to load your registered farms. Please refresh or register a farm first.');
    } finally {
      setLoadingFarms(false);
    }
  };

  const handleFarmChange = (e) => {
    const farmId = parseInt(e.target.value);
    setSelectedFarmId(farmId);
    setFormData({ ...formData, farmId, cropType: '' });

    // Find the selected farm and get its crops
    const selectedFarm = farms.find(f => f.farmId === farmId);
    if (selectedFarm) {
      setAvailableCrops(selectedFarm.cropTypes || []);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePriceTierChange = (index, field, value) => {
    const newTiers = [...priceTiers];
    newTiers[index][field] = value;
    setPriceTiers(newTiers);
  };

  const addPriceTier = () => {
    setPriceTiers([...priceTiers, { minQuantity: '', price: '' }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Get seller ID from localStorage (stored during login)
      let sellerId = parseInt(localStorage.getItem('userId') || '1');
      
      if (!formData.cropType || !formData.state || !formData.basePrice) {
        setSubmitStatus('Please fill in all required fields (Farm, Crop Type, State, Base Price)');
        return;
      }

      if (!selectedFarmId) {
        setSubmitStatus('Please select a farm');
        return;
      }

      // Convert string values to proper types
      const cultivationAreaInAcres = parseFloat(formData.cultivationArea) || 0;
      const harvestQuantityInKg = parseFloat(formData.harvestQuantity) || 0;
      const minimumOrderQuantity = parseInt(formData.moq) || 0;
      const basePrice = parseFloat(formData.basePrice) || 0;

      // Convert price tiers values to proper types
      const convertedPriceTiers = priceTiers.map(tier => ({
        minQuantity: parseInt(tier.minQuantity) || 0,
        price: parseFloat(tier.price) || 0
      }));

      // Validate numeric values
      if (cultivationAreaInAcres <= 0) {
        setSubmitStatus('Cultivation area must be greater than 0');
        return;
      }
      if (harvestQuantityInKg <= 0) {
        setSubmitStatus('Harvest quantity must be greater than 0');
        return;
      }
      if (minimumOrderQuantity <= 0) {
        setSubmitStatus('Minimum order quantity must be greater than 0');
        return;
      }
      if (basePrice <= 0) {
        setSubmitStatus('Base price must be greater than 0');
        return;
      }
      
      const response = await sellerService.createListing(
        sellerId,
        formData.cropType,
        formData.breedVariety,
        formData.farmingDetails,
        cultivationAreaInAcres,
        harvestQuantityInKg,
        minimumOrderQuantity,
        basePrice,
        formData.certificationUrl,
        formData.state,
        formData.region,
        convertedPriceTiers
      );
      setSubmitStatus('✓ Listing created successfully! Pending admin approval.');
      setFormData({
        cropType: '',
        farmId: '',
        breedVariety: '',
        farmingDetails: '',
        cultivationArea: '',
        harvestQuantity: '',
        moq: '',
        basePrice: '',
        certificationUrl: '',
        state: '',
        region: '',
      });
      setPriceTiers([{ minQuantity: '', price: '' }]);
      setSelectedFarmId('');
      setAvailableCrops([]);
      if (onListingCreated) {
        setTimeout(() => onListingCreated(), 1000);
      }
    } catch (error) {
      console.error('Listing creation error:', error);
      
      let errorMessage = 'Failed to create listing. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid data provided. Please check all fields.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Unauthorized. Please login again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to create listings.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSubmitStatus(`Error: ${errorMessage}`);
    }
  };

  return (
    <SellerContainer>
      <h1>Create New Listing</h1>

      {farmsError && <WarningBox>{farmsError}</WarningBox>}

      {loadingFarms ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#667eea' }}>
          Loading your farms...
        </div>
      ) : (
        <Form onSubmit={handleSubmit}>
          {/* Farm & Crop Selection */}
          <FormRow>
            <FormGroup>
              <label>Select Farm * {farms.length === 0 && '(No farms registered)'}</label>
              <select
                value={selectedFarmId}
                onChange={handleFarmChange}
                required
                disabled={farms.length === 0}
              >
                <option value="">-- Choose a farm --</option>
                {farms.map(farm => (
                  <option key={farm.farmId} value={farm.farmId}>
                    {farm.farmName} ({farm.areaInAcres} acres) - {farm.cropTypes.join(', ')}
                  </option>
                ))}
              </select>
            </FormGroup>
            <FormGroup>
              <label>Crop Type * {!selectedFarmId && '(Select farm first)'}</label>
              <select
                name="cropType"
                value={formData.cropType}
                onChange={handleInputChange}
                required
                disabled={availableCrops.length === 0}
              >
                <option value="">-- Choose a crop --</option>
                {availableCrops.map(crop => (
                  <option key={crop} value={crop}>
                    {crop}
                  </option>
                ))}
              </select>
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <label>Breed/Variety *</label>
              <input
                type="text"
                name="breedVariety"
                value={formData.breedVariety}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <label>State *</label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                placeholder="Karnataka"
                required
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <label>Farming Details</label>
            <textarea
              name="farmingDetails"
              value={formData.farmingDetails}
              onChange={handleInputChange}
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <label>Cultivation Area (Acres) *</label>
              <input
              type="number"
              step="0.1"
              name="cultivationArea"
              value={formData.cultivationArea}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
          <FormGroup>
            <label>Harvest Quantity (Kg) *</label>
            <input
              type="number"
              step="0.1"
              name="harvestQuantity"
              value={formData.harvestQuantity}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
        </FormRow>

        <FormRow>
          <FormGroup>
            <label>Minimum Order Quantity (Kg) *</label>
            <input
              type="number"
              name="moq"
              value={formData.moq}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
          <FormGroup>
            <label>Base Price (₹/Kg) *</label>
            <input
              type="number"
              step="0.01"
              name="basePrice"
              value={formData.basePrice}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
        </FormRow>

        <FormGroup>
          <label>Certification URL</label>
          <input
            type="url"
            name="certificationUrl"
            value={formData.certificationUrl}
            onChange={handleInputChange}
            placeholder="https://example.com/certification.pdf"
          />
        </FormGroup>

        <FormRow>
          <FormGroup>
            <label>State *</label>
            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
          <FormGroup>
            <label>Region *</label>
            <input
              type="text"
              name="region"
              value={formData.region}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
        </FormRow>

        <h3>Price Tiers</h3>
        {priceTiers.map((tier, index) => (
          <FormRow key={index}>
            <FormGroup>
              <label>Min Quantity</label>
              <input
                type="number"
                value={tier.minQuantity}
                onChange={(e) => handlePriceTierChange(index, 'minQuantity', e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <label>Price (₹/Kg)</label>
              <input
                type="number"
                step="0.01"
                value={tier.price}
                onChange={(e) => handlePriceTierChange(index, 'price', e.target.value)}
              />
            </FormGroup>
          </FormRow>
        ))}

        <FormGroup>
          <button type="button" onClick={addPriceTier} style={{ background: '#999', marginBottom: '1rem' }}>
            Add Price Tier
          </button>
        </FormGroup>

        {submitStatus && <p style={{ color: submitStatus.includes('Error') ? 'red' : 'green' }}>{submitStatus}</p>}

        <SubmitButton type="submit">Submit Listing for Approval</SubmitButton>
      </Form>
      )}
    </SellerContainer>
  );
};

export default SellerListingForm;
